import React from 'react';
import HostingPackages from '../components/hosting/HostingPackages';
import SEOHelmet from '../components/SEOHelmet';

const Hosting: React.FC = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Web Hosting Services",
    "description": "Professional web hosting with free domain, unlimited bandwidth, and 99.9% uptime guarantee.",
    "brand": {
      "@type": "Brand",
      "name": "12and22"
    },
    "offers": {
      "@type": "AggregateOffer",
      "priceCurrency": "USD",
      "lowPrice": "4.99",
      "highPrice": "29.99"
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHelmet
        title="Web Hosting Plans - Reliable & Affordable Hosting Services | 12and22"
        description="Choose from our reliable web hosting plans starting at $4.99/month. Get free domain, unlimited bandwidth, free SSL certificate, and 99.9% uptime guarantee with 24/7 support."
        keywords="web hosting, cheap hosting, website hosting, shared hosting, WordPress hosting, hosting plans, unlimited hosting, free domain, SSL certificate, 12and22"
        canonicalUrl="https://12and22.com/hosting"
        structuredData={structuredData}
      />
      {/* Hero Section with modern design */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* Badge */}
            <div className="inline-flex items-center px-4 py-2 mb-8 bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-full text-gray-700">
              <span className="w-2 h-2 bg-gradient-to-r from-orange-400 to-yellow-500 rounded-full mr-3"></span>
              <span className="text-sm font-medium">Professional Web Hosting</span>
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black leading-tight mb-8 text-gray-900">
              Web <span className="text-primary-navy">Hosting</span> + 
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500 block">Domain Registration</span>
            </h1>

            {/* Subtitle */}
            <p className="text-xl text-gray-600 mb-10 leading-relaxed max-w-4xl mx-auto">
              Professional web hosting solutions with free domain registration. 
              Perfect for websites, blogs, and applications with enterprise-grade performance.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
              <a
                href="#packages"
                className="bg-gradient-to-br from-primary-navy via-primary-800 to-primary-900 text-white px-8 py-4 rounded-lg font-bold text-lg hover:from-primary-800 hover:via-primary-900 hover:to-primary-navy transition-all duration-200 shadow-lg relative overflow-hidden group"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent group-hover:from-white/30 transition-all duration-200 rounded-lg"></div>
                <span className="relative z-10">View Hosting Plans</span>
              </a>
              
              <a
                href="#features"
                className="border-2 border-orange-400 text-orange-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gradient-to-r hover:from-orange-400 hover:to-yellow-500 hover:text-white hover:border-transparent transition-all duration-200"
              >
                See Features
              </a>
            </div>

            {/* Trust Indicators */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 text-sm max-w-4xl mx-auto">
              {[
                { 
                  icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>, 
                  text: 'SSD Storage', 
                  subtext: 'Lightning Fast' 
                },
                { 
                  icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9-3-9m-9 9a9 9 0 01 9-9" /></svg>, 
                  text: 'Free Domain', 
                  subtext: 'DNS Management' 
                },
                { 
                  icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>, 
                  text: 'Free SSL', 
                  subtext: 'Always Secure' 
                },
                { 
                  icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M12 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>, 
                  text: '24/7 Support', 
                  subtext: 'Expert Help' 
                }
              ].map((item, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <div className={`w-10 h-10 ${index === 1 || index === 2 ? 'bg-gradient-to-br from-orange-400 to-yellow-500' : 'bg-gray-100'} rounded-lg flex items-center justify-center ${index === 1 || index === 2 ? 'text-white' : 'text-gray-600'}`}>
                    {item.icon}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">{item.text}</div>
                    <div className="text-xs text-gray-500">{item.subtext}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      
      <div id="packages">
        <HostingPackages />
      </div>

      {/* Features Section */}
      <section id="features" className="py-20 bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            {/* Badge */}
            <div className="inline-flex items-center px-4 py-2 mb-6 bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-full">
              <span className="w-2 h-2 bg-gradient-to-r from-green-400 to-blue-500 rounded-full mr-3"></span>
              <span className="text-sm font-medium text-gray-700">Everything You Need</span>
            </div>
            
            <h2 className="text-4xl font-black text-gray-900 mb-4">
              Powerful Hosting Features
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to build, deploy, and manage your website with confidence
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>,
                title: 'Lightning Fast Performance',
                description: 'SSD storage, optimized servers, and advanced caching for blazing fast load times'
              },
              {
                icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>,
                title: 'Advanced Security',
                description: 'Free SSL certificates, DDoS protection, and automatic malware scanning'
              },
              {
                icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>,
                title: 'Automated Backups',
                description: 'Daily automated backups with one-click restore to protect your data'
              },
              {
                icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" /></svg>,
                title: 'Developer Friendly',
                description: 'SSH access, Git integration, and support for multiple programming languages'
              },
              {
                icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
                title: 'Global CDN',
                description: 'Content delivery network with 200+ edge locations worldwide for faster loading'
              },
              {
                icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
                title: '99.9% Uptime Guarantee',
                description: 'Industry-leading uptime backed by our Service Level Agreement'
              },
              {
                icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" /></svg>,
                title: 'Easy WordPress Install',
                description: 'One-click WordPress installation with automatic updates and optimization'
              },
              {
                icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>,
                title: 'Professional Email',
                description: 'Create unlimited professional email addresses with your domain'
              },
              {
                icon: <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M12 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>,
                title: '24/7 Expert Support',
                description: 'Round-the-clock support from our experienced hosting specialists'
              }
            ].map((feature, index) => (
              <div key={index} className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-200 relative overflow-hidden group">
                {/* Glossy overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-transparent pointer-events-none rounded-2xl group-hover:from-white/30 transition-all duration-200"></div>
                
                <div className="relative z-10">
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-gradient-to-br from-orange-500 via-yellow-500 to-orange-600 rounded-xl flex items-center justify-center shadow-md relative overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent rounded-xl"></div>
                        <div className="text-white relative z-10">
                          {feature.icon}
                        </div>
                      </div>
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-bold text-gray-900 mb-2">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Hosting;